import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * Terms of Use and Privacy Policy.
 *
 * Two documents, one layout, one file — they share a header, a table of
 * contents and a set of facts (the fee, the escrow rules, the processors), and
 * keeping them apart is how the two drift into contradicting each other.
 *
 * These are **public, unauthenticated pages**: they are linked from the
 * sign-up dialog, and someone deciding whether to sign up must be able to read
 * what they are agreeing to before they have an account.
 *
 * Written to describe what the code actually does — the platform fee (0%), the
 * escrow lifecycle in `api/campaigns.py`, the wallet being a one-way door, the
 * ledger in `api/economy.py`, staff access to messages for dispute and
 * moderation work. A policy that promises something the software does not do
 * is worse than no policy, because it is the promise that gets enforced.
 *
 * Copy is English only, like the landing page it is linked from.
 */

/* ─────────────────────────────────────────────────────────────────────────
 * FILL THESE IN BEFORE LAUNCH.
 *
 * Everything below is either the real product or a placeholder marked as one.
 * The placeholders are not decoration: a policy naming no legal entity, no
 * address and no forum is not enforceable by whoever is meant to be enforcing
 * it, and a privacy notice with no controller identified fails GDPR Art. 13
 * and KVKK Art. 10 on its face. The jurisdiction below is a *guess* from where
 * this project is being built — confirm it against where the entity is
 * actually registered before this page goes live.
 * ───────────────────────────────────────────────────────────────────────── */
const LEGAL = {
    product: 'Astreona',
    entity: '[COMPANY LEGAL NAME]',           // e.g. "Astreona Teknoloji A.Ş."
    address: '[REGISTERED ADDRESS]',
    country: 'Türkiye',
    forum: 'the courts and enforcement offices of İstanbul, Türkiye',
    law: 'the laws of Türkiye',
    email: '[LEGAL CONTACT EMAIL]',           // e.g. legal@astreona.com
    privacyEmail: '[PRIVACY CONTACT EMAIL]',  // may be the same address
    effective: '6 August 2026',
};

/* The one number both documents quote. It has to match `FEE_PERCENT` on the
   landing page and `PLATFORM_FEE_PERCENT` in the backend settings, which is
   what the ledger actually applies — never restate it in prose. */
const FEE_PERCENT = 0;

/* ───────────────────────────────  layout  ─────────────────────────────── */

function Section({ id, title, children }) {
    return (
        <section id={id} className="scroll-mt-24">
            <h2 className="text-[19px] sm:text-[21px] font-black text-slate-900 tracking-tight mt-10 mb-3">
                {title}
            </h2>
            <div className="space-y-3 text-[14.5px] text-slate-600 leading-relaxed">{children}</div>
        </section>
    );
}

function List({ items }) {
    return (
        <ul className="list-none p-0 m-0 space-y-2">
            {items.map((item, index) => (
                <li key={index} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 mt-2 rounded-full bg-primary-500 flex-shrink-0" />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );
}

/** A callout for the clauses someone will be angry about later if they missed
 *  them now. Burying those in a wall of grey text is how a policy loses. */
function Notice({ children }) {
    return (
        <p className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-[13.5px] text-amber-900 leading-relaxed">
            {children}
        </p>
    );
}

function LegalPage({ title, subtitle, sections, children }) {
    useEffect(() => { window.scrollTo(0, 0); }, []);

    return (
        <div className="min-h-screen bg-white">
            <header className="border-b border-slate-200 bg-slate-50">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
                    <Link to="/" className="inline-flex items-center gap-2 text-[13px] font-bold text-slate-500 hover:text-primary-700 no-underline transition">
                        <span aria-hidden="true">←</span> Back to {LEGAL.product}
                    </Link>
                    <h1 className="mt-5 text-[30px] sm:text-[40px] font-black text-slate-900 tracking-tight leading-tight">
                        {title}
                    </h1>
                    <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{subtitle}</p>
                    <p className="mt-4 text-[12.5px] text-slate-500">
                        Effective {LEGAL.effective} · {LEGAL.product} is operated by {LEGAL.entity}, {LEGAL.address}, {LEGAL.country}.
                    </p>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 pb-20">
                <nav className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <p className="text-[11px] font-black text-slate-500 uppercase tracking-[0.14em] mb-3">Contents</p>
                    <ol className="list-none p-0 m-0 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                        {sections.map((section, index) => (
                            <li key={section.id} className="text-[13.5px]">
                                <a href={`#${section.id}`} className="text-slate-600 hover:text-primary-700 no-underline transition">
                                    <span className="text-slate-400 tabular-nums mr-1.5">{index + 1}.</span>
                                    {section.title}
                                </a>
                            </li>
                        ))}
                    </ol>
                </nav>

                {children}

                <div className="mt-14 pt-6 border-t border-slate-200 flex flex-wrap gap-x-6 gap-y-2 text-[13px]">
                    <Link to="/termsofuse" className="font-semibold text-primary-700 hover:underline no-underline">Terms of Use</Link>
                    <Link to="/privacypolicy" className="font-semibold text-primary-700 hover:underline no-underline">Privacy Policy</Link>
                    <Link to="/contactus" className="font-semibold text-slate-500 hover:text-primary-700 no-underline transition">Contact us</Link>
                </div>
            </main>
        </div>
    );
}

/* ──────────────────────────────  the terms  ───────────────────────────── */

const TERMS_SECTIONS = [
    { id: 'accept', title: 'Accepting these terms' },
    { id: 'eligibility', title: 'Who may use Astreona' },
    { id: 'account', title: 'Your account' },
    { id: 'role', title: 'What Astreona is — and is not' },
    { id: 'creators', title: 'If you are a creator' },
    { id: 'brands', title: 'If you are a brand' },
    { id: 'campaigns', title: 'Campaigns, escrow and disputes' },
    { id: 'fees', title: 'Fees' },
    { id: 'wallet', title: 'Wallet, stars and coffees' },
    { id: 'payouts', title: 'Earnings and payouts' },
    { id: 'chargebacks', title: 'Chargebacks, reversals and set-off' },
    { id: 'conduct', title: 'Prohibited conduct' },
    { id: 'circumvention', title: 'Taking business off the platform' },
    { id: 'content', title: 'Your content and the licence you grant' },
    { id: 'ratings', title: 'Ratings, reviews and reputation' },
    { id: 'moderation', title: 'Moderation, suspension and termination' },
    { id: 'thirdparty', title: 'Third-party services' },
    { id: 'noguarantee', title: 'No earnings guarantee' },
    { id: 'warranty', title: 'Service provided “as is”' },
    { id: 'liability', title: 'Limitation of liability' },
    { id: 'indemnity', title: 'Indemnity' },
    { id: 'changes', title: 'Changes to the service and to these terms' },
    { id: 'law', title: 'Governing law and disputes' },
    { id: 'misc', title: 'General' },
];

export function TermsOfUse() {
    return (
        <LegalPage
            title="Terms of Use"
            subtitle={`The agreement between you and ${LEGAL.entity} for using ${LEGAL.product}. It covers money, so please read sections 7 to 11 properly.`}
            sections={TERMS_SECTIONS}
        >
            <Section id="accept" title="1. Accepting these terms">
                <p>
                    By creating an account, or by using {LEGAL.product} in any way, you agree to these Terms of Use
                    and to our <Link to="/privacypolicy" className="text-primary-700 font-semibold no-underline hover:underline">Privacy Policy</Link>.
                    If you do not agree, do not use the service.
                </p>
                <p>
                    If you accept these terms on behalf of a company, you confirm you are authorised to bind it,
                    and “you” means that company.
                </p>
            </Section>

            <Section id="eligibility" title="2. Who may use Astreona">
                <List items={[
                    'You must be at least 18 years old and legally able to enter into contracts.',
                    'You must give accurate registration details and keep them current. We may verify them at any time.',
                    'One person or company, one account, unless we agree otherwise in writing.',
                    'You must not use the service where doing so would breach the law that applies to you, or where you are subject to sanctions that prohibit us from dealing with you.',
                ]} />
            </Section>

            <Section id="account" title="3. Your account">
                <p>
                    You are responsible for everything done through your account and for keeping your password safe.
                    Tell us immediately if you suspect someone else has access.
                </p>
                <p>
                    Your username becomes your public link. We may reclaim a username that impersonates someone,
                    infringes a trade mark, is used to squat, or belongs to an account that has never been used.
                </p>
            </Section>

            <Section id="role" title="4. What Astreona is — and is not">
                <p>
                    {LEGAL.product} is a technology platform. We provide the tools — profiles, a campaign marketplace,
                    messaging, an escrow mechanism, a ledger and payout administration — that let creators and brands
                    find each other and transact.
                </p>
                <Notice>
                    We are not a party to the agreement between a creator and a brand. We do not employ creators,
                    we are not an agency, and we do not act as anyone’s representative. We do not guarantee the
                    quality, legality or timeliness of anything a user delivers, or that any brand will approve
                    a delivery. Where we operate escrow or arbitrate a dispute, we do so as an administrator of a
                    process both sides agreed to — not as a guarantor of either side.
                </Notice>
                <p>
                    We are not a bank. Balances held on {LEGAL.product} are not deposits, are not insured, and earn
                    no interest.
                </p>
            </Section>

            <Section id="creators" title="5. If you are a creator">
                <List items={[
                    'You own or have the rights to everything you publish and deliver, including music, footage, fonts and any third-party material in it.',
                    'You are responsible for disclosing paid partnerships and advertising as the law where your audience is requires. That obligation is yours, not ours, and no brand instruction overrides it.',
                    'Follower counts, reach and engagement figures you enter are self-reported. Entering figures you know to be false — or bought engagement — is a breach of these terms and grounds for removal of earnings obtained through it.',
                    'You are responsible for your own taxes, social security and any registration your local law requires. We do not withhold on your behalf unless the law obliges us to.',
                    'You must deliver what you agreed to deliver, by the date you agreed to deliver it.',
                ]} />
            </Section>

            <Section id="brands" title="6. If you are a brand">
                <List items={[
                    'You must have the right to the brand, product and materials you ask a creator to feature.',
                    'A campaign brief must be accurate and complete. Requirements you did not state cannot be the basis of a rejection later.',
                    'You must fund a collaboration before work starts — approval debits your wallet on the spot. If your wallet cannot cover it, the approval does not happen.',
                    'You must review a delivery within the stated window. If you do not, it auto-approves and the escrow releases to the creator.',
                    'Usage rights are what the agreement says they are. Using content beyond them is a matter between you and the creator, and we may suspend an account that does it repeatedly.',
                ]} />
            </Section>

            <Section id="campaigns" title="7. Campaigns, escrow and disputes">
                <p>These are the rules the software actually enforces, and by using the marketplace you accept them:</p>
                <List items={[
                    'Approving an application debits the brand’s wallet and locks the amount in escrow, in one atomic step. If the funding fails, the approval fails with it.',
                    'A delivery carries an auto-approval deadline. If the brand does not review it in time, the escrow releases to the creator automatically.',
                    'A revision request must state a reason. An unexplained rejection is refused by the system.',
                    'Revision rounds are limited. A dispute can only be opened once those rounds are spent — a brand cannot take delivery and escalate straight to a refund.',
                    'Opening a dispute freezes the escrow. It does not return the money to the brand.',
                    'A frozen escrow is settled only by our arbitration desk: to the creator, to the brand, or split by percentage.',
                ]} />
                <Notice>
                    An arbitration decision is final as far as {LEGAL.product} is concerned. It resolves where the
                    escrowed money goes; it is not a judgment, and it does not stop either side pursuing the other
                    elsewhere. We decide on the record in front of us — the brief, the contract snapshot, the
                    delivery, the revision notes and the timeline — so keep the relevant exchange on the platform.
                </Notice>
                <p>
                    At approval we issue a collaboration agreement that freezes the brief, the amount, the usage
                    rights and the fee split, and hash-stamps them. Later edits to a campaign do not change an
                    agreement already issued.
                </p>
            </Section>

            <Section id="fees" title="8. Fees">
                <p>
                    Joining is free, there is no monthly charge, and our platform fee is currently {FEE_PERCENT}% —
                    we take no commission from income you earn through {LEGAL.product}. Every ledger row still
                    shows a fee column so the rate in force is always visible; at present it reads zero.
                </p>
                <List items={[
                    'The fee percentage that applies to a collaboration is the one recorded in its agreement at approval. Changing our published rate afterwards does not rewrite it.',
                    'Fees already taken, if any, are not refundable, including where a payment is later reversed for reasons outside our control.',
                    'Payment processors, banks and currency conversion may charge their own fees. Those are not ours and we do not control them.',
                    'Where the law requires us to withhold tax, we withhold it and the net is what reaches you.',
                    'We may change the published fee rate for future transactions with at least 30 days’ notice by email or in the app.',
                ]} />
            </Section>

            <Section id="wallet" title="9. Wallet, stars and coffees">
                <p>
                    The <strong className="text-slate-800">wallet</strong> is spendable balance a brand tops up by card.
                    It funds campaign escrow and receives escrow refunds.
                </p>
                <Notice>
                    Wallet balance is <strong>not withdrawable</strong>. A card top-up cannot be converted back into
                    a payout — that would make the payout queue a route for laundering money, so the door only opens
                    one way. If you have an unspent balance and a genuine reason to recover it, contact us and we
                    will consider it case by case; we are not obliged to agree.
                </Notice>
                <p>
                    <strong className="text-slate-800">Stars</strong> and <strong className="text-slate-800">coffees</strong> are
                    features of the service, not currency. They have no value outside {LEGAL.product}, cannot be
                    exchanged between users for money, and purchases of them are final and non-refundable except
                    where the law says otherwise. Stars you receive as a gift are booked to your earnings and can be
                    withdrawn like any other income.
                </p>
                <p>
                    Subscriptions renew until cancelled. Cancelling stops the next renewal; it does not refund the
                    period you are in.
                </p>
            </Section>

            <Section id="payouts" title="10. Earnings and payouts">
                <List items={[
                    'Your earnings ledger is the record. Every income event appends a row showing gross, fee and net, and your dashboard is an aggregate of those rows.',
                    'You may request a payout of your available balance. A minimum amount, identity verification and payment-provider requirements may apply before we release funds.',
                    'We may hold a payout while we investigate suspected fraud, a chargeback, a dispute, a sanctions match, or a legal request — and for as long as that investigation reasonably takes.',
                    'You are responsible for the payout details you give us. We are not liable for money sent to an account you entered incorrectly.',
                    'Payout timing depends on banks and providers. Any estimate we show is an estimate.',
                ]} />
            </Section>

            <Section id="chargebacks" title="11. Chargebacks, reversals and set-off">
                <p>
                    If a payment that funded your earnings is reversed — a chargeback, a failed settlement, a
                    fraudulent card — the corresponding amount is no longer yours, whether or not it has already
                    been credited to you.
                </p>
                <List items={[
                    'We may reverse the ledger entry, deduct the amount from your balance, or set it off against future earnings.',
                    'If your balance does not cover it, the shortfall is a debt you owe us and we may recover it.',
                    'We may charge a reasonable administrative amount for handling a chargeback caused by your breach of these terms.',
                    'Deliberately triggering chargebacks after receiving work or content is fraud, and we treat it as such.',
                ]} />
            </Section>

            <Section id="conduct" title="12. Prohibited conduct">
                <p>You must not:</p>
                <List items={[
                    'Break the law, infringe anyone’s intellectual property, or publish content you have no right to publish.',
                    'Post sexual content involving minors, non-consensual content, or anything that sexualises a real person without their consent — this ends an account immediately and is reported.',
                    'Harass, threaten, dox or impersonate anyone, including impersonating a brand you have no relationship with.',
                    'Use the service for money laundering, sanctions evasion, terrorist financing, or to move money that has nothing to do with a genuine collaboration.',
                    'Inflate metrics: bought followers, bot engagement, fake reviews, or self-dealing between accounts you control.',
                    'File a campaign, a delivery or a dispute in bad faith, or fabricate evidence in one.',
                    'Scrape, crawl, reverse-engineer, resell or automate access to the service without our written permission.',
                    'Interfere with the service, probe it for vulnerabilities without authorisation, or work around a suspension.',
                ]} />
            </Section>

            <Section id="circumvention" title="13. Taking business off the platform">
                <p>
                    You may work with anyone you like, anywhere you like. What you may not do is use {LEGAL.product} to
                    find a counterparty or negotiate a collaboration and then move that specific deal off the platform
                    to settle it outside our escrow — including asking for payment by bank transfer, gift card or
                    another platform after contact was made here.
                </p>
                <p>
                    If you do, we may charge any fee that would have applied, suspend or close the account, and
                    withhold amounts owed to you up to that amount. Escrow, the collaboration agreement, the
                    auto-approval deadline and the arbitration desk are the protections you give up by going
                    off-platform, and none of them costs you a commission to use.
                </p>
            </Section>

            <Section id="content" title="14. Your content and the licence you grant">
                <p>
                    You keep ownership of everything you upload. We do not claim your work, and we do not sell it.
                </p>
                <p>
                    You grant us a worldwide, non-exclusive, royalty-free licence to host, store, reproduce, adapt
                    for formatting purposes, and display your content <em>for the purpose of operating the service</em> —
                    showing your profile and media kit, delivering messages, rendering agreements, and running backups.
                </p>
                <p>
                    Separately, we may feature public content from your profile in {LEGAL.product}’s own marketing.
                    You can withdraw that permission at any time by emailing {LEGAL.email}, and we will stop using it
                    in new material.
                </p>
                <p>
                    The licence ends when you delete the content, except for copies retained in backups for a limited
                    period and copies we must keep by law, and except where someone has already received the content
                    as part of a completed collaboration.
                </p>
            </Section>

            <Section id="ratings" title="15. Ratings, reviews and reputation">
                <List items={[
                    'A rating can only be filed on a collaboration that settled through our escrow, by one of the two accounts that were in it.',
                    'Ratings are double-blind: neither is published until both are filed or the blind window closes.',
                    'A published rating is permanent. A low one must carry a written reason.',
                    'Factual figures we publish — review times, lapsed deadlines, early deliveries — come from our own transaction records and are not opinions. We do not remove them because they are unflattering.',
                    'We may remove a rating that is abusive, off-topic, contains personal data, or was filed to manipulate a score. We are not obliged to remove one you simply disagree with.',
                ]} />
            </Section>

            <Section id="moderation" title="16. Moderation, suspension and termination">
                <p>
                    We may remove content, limit features, suspend or close an account where we reasonably believe
                    these terms have been breached, where we are required to by law, or where continuing would expose
                    us or other users to risk of harm, fraud or legal liability.
                </p>
                <p>
                    Reports made against an account are confidential. We do not tell the reported account that a
                    report exists, or who made it.
                </p>
                <p>
                    You can close your account at any time. On closure, whether by you or by us: collaborations
                    already in escrow run to their conclusion under these terms; earnings genuinely owed to you remain
                    payable, subject to the holds in sections 10 and 11; and no fee already taken is refunded. If your
                    account is closed for fraud or for a breach of section 12, we may withhold amounts derived from
                    that conduct.
                </p>
            </Section>

            <Section id="thirdparty" title="17. Third-party services">
                <p>
                    Payments are handled by Stripe, media is hosted and delivered by Cloudflare, and email is sent
                    through Mailgun. Signing in with Google is optional and handled by Google. Using {LEGAL.product}
                    means those providers process your data under their own terms as well. We choose them carefully,
                    but we do not control their outages, decisions or pricing.
                </p>
            </Section>

            <Section id="noguarantee" title="18. No earnings guarantee">
                <Notice>
                    Every figure shown on our marketing pages is an illustration on an example account. It is not a
                    projection, a promise or a typical result. What you earn depends on your audience, your work,
                    your prices and demand from brands. Most of that is outside our control and none of it is
                    guaranteed.
                </Notice>
            </Section>

            <Section id="warranty" title="19. Service provided “as is”">
                <p>
                    We work to keep {LEGAL.product} available and correct, but we provide it “as is” and “as
                    available”. To the fullest extent the law allows, we exclude all implied warranties, including
                    fitness for a particular purpose and uninterrupted or error-free operation. Features change and
                    some are removed.
                </p>
                <p>
                    Nothing here limits rights you have as a consumer that cannot be limited by agreement.
                </p>
            </Section>

            <Section id="liability" title="20. Limitation of liability">
                <p>
                    To the fullest extent permitted by law, we are not liable for indirect or consequential loss,
                    lost profits, lost opportunities, lost audience, lost data, or the acts and omissions of other
                    users — including a brand that refuses a delivery or a creator that fails to deliver.
                </p>
                <p>
                    Our total liability to you for all claims arising out of or relating to the service in any
                    12-month period is limited to the greater of (a) the platform fees you paid us in that period,
                    and (b) USD 100.
                </p>
                <p>
                    Where the claim concerns a specific collaboration, our liability is additionally capped at the
                    amount held in escrow for it.
                </p>
                <p>
                    We do not exclude liability for fraud, for death or personal injury caused by our negligence, or
                    for anything else that cannot lawfully be excluded.
                </p>
            </Section>

            <Section id="indemnity" title="21. Indemnity">
                <p>
                    You will indemnify {LEGAL.entity} against claims, losses and reasonable legal costs arising from
                    your content, your collaborations, your breach of these terms, or your breach of anyone’s rights —
                    including advertising-disclosure and intellectual-property claims brought by a regulator or a
                    third party over something you published.
                </p>
            </Section>

            <Section id="changes" title="22. Changes to the service and to these terms">
                <p>
                    We may change the service, and we may change these terms. For material changes we will give at
                    least 30 days’ notice by email or in the app before they take effect. Continuing to use
                    {' '}{LEGAL.product} after that is acceptance. If you do not accept, close your account and
                    request any balance owed to you.
                </p>
                <p>
                    Changes required by law or needed to address a security or fraud risk may take effect immediately.
                </p>
            </Section>

            <Section id="law" title="23. Governing law and disputes">
                <p>
                    These terms are governed by {LEGAL.law}, and {LEGAL.forum} have exclusive jurisdiction —
                    except that a consumer may also bring proceedings where they are resident, if the law gives them
                    that right.
                </p>
                <p>
                    Before starting proceedings, please contact us at {LEGAL.email}. Most disputes are resolved
                    faster that way, and we will engage with yours in good faith for 30 days.
                </p>
                <p>
                    Claims must be brought individually. Class actions and representative proceedings are not
                    available to the extent the law allows them to be waived.
                </p>
            </Section>

            <Section id="misc" title="24. General">
                <List items={[
                    'These terms and the Privacy Policy are the whole agreement between us about the service.',
                    'If a clause is found unenforceable, the rest stays in force.',
                    'Not enforcing a right immediately does not waive it.',
                    'You may not transfer your rights under these terms. We may transfer ours to a successor of our business, on notice.',
                    'Nothing in these terms creates a partnership, joint venture, employment or agency relationship.',
                ]} />
                <p>Questions: {LEGAL.email}, or via our <Link to="/contactus" className="text-primary-700 font-semibold no-underline hover:underline">contact page</Link>.</p>
            </Section>
        </LegalPage>
    );
}

/* ────────────────────────────  privacy policy  ───────────────────────── */

const PRIVACY_SECTIONS = [
    { id: 'who', title: 'Who is responsible for your data' },
    { id: 'collect', title: 'What we collect' },
    { id: 'use', title: 'What we use it for' },
    { id: 'basis', title: 'Our legal bases' },
    { id: 'public', title: 'What is public, and what is not' },
    { id: 'messages', title: 'Messages and staff access' },
    { id: 'share', title: 'Who we share it with' },
    { id: 'transfers', title: 'International transfers' },
    { id: 'retention', title: 'How long we keep it' },
    { id: 'rights', title: 'Your rights' },
    { id: 'cookies', title: 'Cookies and local storage' },
    { id: 'security', title: 'Security' },
    { id: 'children', title: 'Children' },
    { id: 'privacychanges', title: 'Changes to this policy' },
    { id: 'contactprivacy', title: 'Contact and complaints' },
];

export function PrivacyPolicy() {
    return (
        <LegalPage
            title="Privacy Policy"
            subtitle="What we collect, why we hold it, who else sees it, and what you can ask us to do about it."
            sections={PRIVACY_SECTIONS}
        >
            <Section id="who" title="1. Who is responsible for your data">
                <p>
                    {LEGAL.entity}, {LEGAL.address}, {LEGAL.country}, is the data controller for personal data
                    processed through {LEGAL.product}. Contact us about privacy at {LEGAL.privacyEmail}.
                </p>
            </Section>

            <Section id="collect" title="2. What we collect">
                <List items={[
                    <><strong className="text-slate-800">Account data</strong> — username, email address, password (stored hashed, never in readable form), account type, verification status, language.</>,
                    <><strong className="text-slate-800">Profile and shopfront</strong> — photo, bio, tags, countries, gallery, social links, media kit, rate card, availability. Figures you enter about your reach are self-reported.</>,
                    <><strong className="text-slate-800">Transaction data</strong> — campaigns, applications, deliveries, escrow movements, ledger rows, wallet transactions, payouts, subscriptions, stars and coffees. This is financial record-keeping, and we keep it.</>,
                    <><strong className="text-slate-800">Payment data</strong> — handled by Stripe. We receive confirmations, identifiers and the last digits and brand of a card. We never see or store full card numbers.</>,
                    <><strong className="text-slate-800">Messages</strong> — the content of chats you send through the platform, and who you sent them to.</>,
                    <><strong className="text-slate-800">Uploads</strong> — images and video you upload, stored and served by Cloudflare.</>,
                    <><strong className="text-slate-800">Technical data</strong> — IP address, device and browser information, timestamps, and logs of requests and errors.</>,
                    <><strong className="text-slate-800">Notification tokens</strong> — a push token if you enable notifications on mobile.</>,
                    <><strong className="text-slate-800">Reports and moderation records</strong> — reports you file or that concern you, and what we did about them.</>,
                ]} />
            </Section>

            <Section id="use" title="3. What we use it for">
                <List items={[
                    'Running your account, your public page and the features you switch on.',
                    'Processing payments, holding escrow, keeping the earnings ledger and administering payouts.',
                    'Detecting and preventing fraud, chargebacks, fake engagement, laundering and abuse — including analysing patterns across accounts.',
                    'Investigating disputes and moderation reports, and arbitrating where escrow is frozen.',
                    'Sending transactional email you asked for by acting: verification codes, a welcome mail, and a notice that you have an unread message.',
                    'Sending push notifications if you enabled them.',
                    'Ranking discovery and suggestions — the scorer uses your stated categories, platforms, availability and published prices.',
                    'Support, and improving the product from aggregate usage.',
                    'Meeting our legal, tax and accounting obligations, and responding to lawful requests.',
                ]} />
                <p>
                    We do not sell your personal data, and we do not run third-party advertising networks on the
                    platform.
                </p>
            </Section>

            <Section id="basis" title="4. Our legal bases">
                <List items={[
                    <><strong className="text-slate-800">Performance of a contract</strong> — everything needed to give you the service you signed up for.</>,
                    <><strong className="text-slate-800">Legal obligation</strong> — financial records, tax, anti-money-laundering, lawful requests.</>,
                    <><strong className="text-slate-800">Legitimate interests</strong> — fraud prevention, platform security, dispute resolution, and improving the product. We balance these against your rights and you can object (section 10).</>,
                    <><strong className="text-slate-800">Consent</strong> — push notifications, optional marketing, and any use of your content in our own marketing. You can withdraw consent at any time.</>,
                ]} />
            </Section>

            <Section id="public" title="5. What is public, and what is not">
                <p>
                    <strong className="text-slate-800">Public by design:</strong> your username and profile page, your
                    photo, bio, tags and social links, your published rate card, your media kit once you publish it or
                    share its link, your ratings once they are revealed, and the fact that you took part in a
                    collaboration where the counterparty chose to show it.
                </p>
                <p>
                    <strong className="text-slate-800">Never public:</strong> your email address, your balances and
                    ledger, what you were paid on any collaboration, your payout details, your messages, and reports
                    filed by or about you.
                </p>
                <p>
                    A media kit can also be seen by any brand you have applied to — applying is what shares it. Your
                    share token is stripped from anything a non-owner receives.
                </p>
            </Section>

            <Section id="messages" title="6. Messages and staff access">
                <p>
                    Messages are stored on our servers so they can be delivered and shown to you. They are not
                    end-to-end encrypted.
                </p>
                <Notice>
                    Authorised staff can access message content where it is necessary to investigate a report, resolve
                    a dispute over frozen escrow, respond to a lawful request, or address fraud or a security
                    incident. We do not read messages routinely, and we do not use their content for advertising.
                </Notice>
            </Section>

            <Section id="share" title="7. Who we share it with">
                <List items={[
                    <><strong className="text-slate-800">Payments</strong> — Stripe, for card processing, wallet top-ups, subscriptions and fraud checks.</>,
                    <><strong className="text-slate-800">Media</strong> — Cloudflare, for storing and delivering images and video.</>,
                    <><strong className="text-slate-800">Email</strong> — Mailgun, for transactional mail.</>,
                    <><strong className="text-slate-800">Sign-in</strong> — Google, only if you choose to sign in with it.</>,
                    <><strong className="text-slate-800">Hosting and infrastructure</strong> — our hosting and database providers, under contract and on our instructions.</>,
                    <><strong className="text-slate-800">The other side of a collaboration</strong> — what section 5 makes visible to them, and what you send them.</>,
                    <><strong className="text-slate-800">Advisers and authorities</strong> — where the law requires it, or to establish, exercise or defend legal claims.</>,
                    <><strong className="text-slate-800">A successor</strong> — if the business is sold or merged, on notice to you.</>,
                ]} />
            </Section>

            <Section id="transfers" title="8. International transfers">
                <p>
                    Our providers operate globally, so your data may be processed outside {LEGAL.country}. Where it
                    is, we rely on the safeguards available to us — an adequacy decision, standard contractual
                    clauses, or an equivalent mechanism — and we contract with providers that offer them.
                </p>
            </Section>

            <Section id="retention" title="9. How long we keep it">
                <List items={[
                    'Account and profile data: while your account exists, then deleted or anonymised within a reasonable period after closure.',
                    'Financial records — ledger rows, escrow movements, payouts, invoices: kept for the period our tax and accounting law requires, even after you close your account. We cannot delete these on request.',
                    'Messages: while the conversation exists, and longer where they are evidence in an open dispute or investigation.',
                    'Moderation and fraud records: as long as needed to keep the decision enforceable and prevent repetition.',
                    'Logs: a short rolling period for security and debugging.',
                ]} />
            </Section>

            <Section id="rights" title="10. Your rights">
                <p>Depending on where you live, you can ask us to:</p>
                <List items={[
                    'Give you a copy of the personal data we hold about you, and tell you how we process it.',
                    'Correct data that is wrong or incomplete — much of it you can edit yourself in settings.',
                    'Delete your data, subject to the records we are legally required to keep.',
                    'Restrict or object to processing based on our legitimate interests.',
                    'Receive your data in a portable format.',
                    'Withdraw a consent you gave, without affecting what we did before you withdrew it.',
                ]} />
                <p>
                    Email {LEGAL.privacyEmail} and we will respond within the period the law sets — one month under
                    GDPR, 30 days under KVKK. We may ask you to verify your identity first, because handing an
                    account’s data to the wrong person is the harm these rights exist to prevent.
                </p>
            </Section>

            <Section id="cookies" title="11. Cookies and local storage">
                <p>
                    We use local storage to keep you signed in and to remember your language, and cookies that are
                    necessary for the site and for Stripe’s fraud checks to work. We do not use third-party
                    advertising or cross-site tracking cookies.
                </p>
                <p>
                    Our transactional email has click and open tracking switched off.
                </p>
            </Section>

            <Section id="security" title="12. Security">
                <p>
                    Passwords are hashed. Access to production data is restricted to staff who need it. Sessions use
                    signed tokens that can be revoked, and a revoked token is rejected on every request. Payments run
                    through Stripe so card numbers never touch our servers.
                </p>
                <p>
                    No service is perfectly secure. If a breach affects your personal data and the law requires it,
                    we will notify you and the relevant authority within the required time.
                </p>
            </Section>

            <Section id="children" title="13. Children">
                <p>
                    {LEGAL.product} is not for anyone under 18. We do not knowingly collect data from children, and
                    we delete an account and its data if we learn it belongs to one.
                </p>
            </Section>

            <Section id="privacychanges" title="14. Changes to this policy">
                <p>
                    We will post any change here and update the effective date. For material changes we will notify
                    you by email or in the app before they take effect.
                </p>
            </Section>

            <Section id="contactprivacy" title="15. Contact and complaints">
                <p>
                    Privacy questions and rights requests: {LEGAL.privacyEmail}. Anything else: our{' '}
                    <Link to="/contactus" className="text-primary-700 font-semibold no-underline hover:underline">contact page</Link>.
                </p>
                <p>
                    If you are not satisfied with our answer, you can complain to your local data protection
                    authority — in {LEGAL.country}, the Personal Data Protection Authority (KVKK).
                </p>
            </Section>
        </LegalPage>
    );
}

export default TermsOfUse;
